import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DuctRun, type DuctStage } from '@monrdv/ui';
import {
  formatMoney,
  formatPhone,
  formatSpanExact,
  groupIntoPeriods,
  localized,
  toCents,
  toE164,
  type Locale,
  type StageKind,
} from '@monrdv/shared';
import { request } from '../lib/api.js';
import { AttachmentSection } from '../components/Attachments.js';
import { Timeline, type TimelineEvent } from '../components/Timeline.js';

/**
 * The customer page — the whole relationship, on one page.
 *
 * This screen is the answer to the worst flaw in the system it replaces: there, one
 * customer's jobs, quotes, invoices, calls and messages lived on a dozen separate screens,
 * so answering "why is this person calling me?" meant opening eight tabs and holding the
 * answer in your head. Here it is one scrollable page, in the order a coordinator needs it:
 * who they are and how to reach them, what work is open, what was quoted, what is owed,
 * what was said on the phone, what was written.
 *
 * The `tab` search parameter therefore does **not** hide anything. Every section is on the
 * page always; the tab only chooses which one shows every row instead of the first few. That
 * keeps a deep link (`?tab=calls&call=<id>`) useful — it lands you on the call log — without
 * turning the page back into six screens wearing a tab bar.
 *
 * One query per section rather than one composite endpoint, because live invalidation is
 * per-entity: a colleague issuing an invoice must refresh the invoice section through the
 * `['invoices']` prefix without refetching the calls, the messages and the header with it.
 */

// --- assumed API shapes -----------------------------------------------------------------
// None of these endpoints exist yet. The types are the responses each section needs, named
// the way the rest of the codebase names things (camelCase, `id`, `<verb>At`, money as
// `numeric(14,2)` strings rather than floats).

/** `GET /api/customers/:id` — the whole identity, contact and money header in one call. */
type CustomerDetail = {
  id: string;
  number: string | null;
  displayName: string;
  organization: string | null;
  /** The language this customer is written to — quotes, invoices and SMS follow it. */
  locale: Locale;
  status: 'active' | 'inactive' | 'archived';
  /** Staff-authored prose about the relationship. Never translated, never rendered to them. */
  note: string | null;
  billToParent: boolean;
  /** Set when this account belongs to a larger one (a property manager, a chain). */
  parent: { id: string; displayName: string; organization: string | null } | null;
  phones: Array<{
    id: string;
    kind: string;
    raw: string;
    extension: string | null;
    isPrimary: boolean;
    /** False means texting this number is not permitted, whatever the workflow wants. */
    smsConsent: boolean;
  }>;
  emails: Array<{ id: string; kind: string; email: string; isPrimary: boolean; bounced: boolean }>;
  addresses: Array<{
    id: string;
    kind: string;
    label: string | null;
    line1: string;
    line2: string | null;
    city: string;
    region: string;
    postalCode: string | null;
    /** Gate codes, "sonner deux fois", where to park. The reason technicians open this page. */
    note: string | null;
    isPrimary: boolean;
    unit: string | null;
    /** Customers we hold at this same dwelling — the previous owner, usually. */
    othersAtPlace: number;
    /** Everyone else in the building, which is a different question entirely. */
    othersInBuilding: number;
  }>;
  tags: Array<{ id: string; name: string; color: string }>;
  /** `numeric(14,2)` strings, converted with `toCents` at this boundary. */
  balance: { open: string; overdue: string; unappliedCredit: string };
};

/** `GET /api/jobs?customerId=…&open=true` — the work still needing someone to act. */
type CustomerJobsResponse = {
  items: Array<{
    id: string;
    number: string;
    title: string | null;
    stageId: string;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    assignees: Array<{ id: string; name: string }>;
    balance: string;
    lastActivityAt: string | null;
  }>;
  /**
   * The tenant's pipeline, returned with the jobs so the duct run can be drawn without a
   * second round trip. Stages are per-tenant data, not a fixed list, so they must travel
   * with anything that renders a stage.
   */
  pipeline: Array<{ id: string; name: Record<string, string>; kind: StageKind; position: number }>;
  total: number;
};

/** `GET /api/quotes?customerId=…` */
type CustomerQuotesResponse = {
  items: Array<{
    id: string;
    number: string;
    status: 'draft' | 'pending' | 'accepted' | 'declined' | 'expired';
    total: string;
    issuedOn: string | null;
    expiresOn: string | null;
    acceptedAt: string | null;
    jobId: string | null;
    jobNumber: string | null;
  }>;
  total: number;
};

/** `GET /api/invoices?customerId=…` */
type CustomerInvoicesResponse = {
  items: Array<{
    id: string;
    number: string;
    status: 'draft' | 'awaiting_payment' | 'partial' | 'paid' | 'voided';
    total: string;
    paidTotal: string;
    balance: string;
    issuedOn: string | null;
    dueOn: string | null;
    /** Null means they never opened it — the first thing to check before chasing payment. */
    firstViewedAt: string | null;
    jobId: string | null;
    jobNumber: string | null;
  }>;
  /** Sum of the balances of every unpaid invoice, so the section footer needs no arithmetic. */
  outstanding: string;
  total: number;
};

/** `GET /api/calls?customerId=…` — summaries only; the verbatim transcript is not sent. */
type CustomerCallsResponse = {
  items: Array<{
    id: string;
    direction: 'inbound' | 'outbound';
    status: 'ringing' | 'in_progress' | 'completed' | 'no_answer' | 'busy' | 'failed';
    startedAt: string;
    durationSeconds: number;
    handledByName: string | null;
    /** One paragraph. The verbatim transcript is deliberately not fetched here. */
    transcriptSummary: string | null;
    hasRecording: boolean;
    hasTranscript: boolean;
    jobId: string | null;
    jobNumber: string | null;
  }>;
  total: number;
};

/** `GET /api/customers/:id/thread` — the customer-level conversation, newest messages last. */
type CustomerThreadResponse = {
  thread: { id: string; subject: string | null; messageCount: number; lastMessageAt: string | null } | null;
  /** Newest last, so the preview reads like a conversation rather than a log. */
  messages: Array<{
    id: string;
    authorKind: 'staff' | 'customer' | 'system';
    authorName: string | null;
    transport: 'sms' | 'email' | 'in_app' | 'note' | 'call';
    body: string | null;
    sentAt: string;
    readAt: string | null;
    failedReason: string | null;
  }>;
};

/**
 * Enough rows that expanding a section rarely needs another request, and few enough that the
 * page stays one page. Sections show `PREVIEW` rows until the tab selects them, and the
 * remainder is sliced client-side — expanding must be instant, not a spinner.
 */
const FETCH_LIMIT = 25;
const PREVIEW = 3;
const PREVIEW_MESSAGES = 4;

/**
 * Stage colour comes from the stage's `kind`, never from the per-tenant hex the three
 * businesses arrived with — same rule the jobs screen follows, so "awaiting payment" is one
 * amber everywhere and every colour on screen traces back to a token.
 */
const STAGE_COLOR: Record<StageKind, string> = {
  intake: 'var(--stage-intake)',
  quoting: 'var(--stage-quoting)',
  scheduled: 'var(--stage-scheduled)',
  in_progress: 'var(--stage-in-progress)',
  awaiting_payment: 'var(--stage-awaiting-payment)',
  won: 'var(--stage-won)',
  lost: 'var(--stage-lost)',
  cancelled: 'var(--stage-cancelled)',
};

/**
 * Document status colours come from the same tokens for the same reason: an invoice awaiting
 * payment is the amber of the pipeline stage that means the same thing, so staff learn one
 * colour vocabulary instead of two.
 */
const QUOTE_STATUS_COLOR: Record<CustomerQuotesResponse['items'][number]['status'], string> = {
  draft: 'var(--text-secondary)',
  pending: 'var(--stage-quoting)',
  accepted: 'var(--state-positive)',
  declined: 'var(--state-negative)',
  expired: 'var(--text-muted)',
};

const INVOICE_STATUS_COLOR: Record<CustomerInvoicesResponse['items'][number]['status'], string> = {
  draft: 'var(--text-secondary)',
  awaiting_payment: 'var(--state-caution)',
  partial: 'var(--stage-in-progress)',
  paid: 'var(--state-positive)',
  voided: 'var(--state-negative)',
};

/** Language names are proper nouns: they are written in the language they name, not translated. */
const LOCALE_NAME: Record<Locale, string> = { fr: 'Français', en: 'English' };

export function CustomerDetailScreen() {
  const { t: translate, i18n } = useTranslation();
  // Same cast the shell uses: the catalogue is not type-augmented, and everything below only
  // needs `(key, vars) => string`.
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { customerId } = useParams({ from: '/customers/$customerId' });
  const { tab, call } = useSearch({ from: '/customers/$customerId' });

  // `['customer', id]` is the exact key the realtime layer invalidates for this record.
  const customer = useQuery({
    queryKey: ['customer', customerId],
    queryFn: ({ signal }) => request<CustomerDetail>(`/customers/${customerId}`, { signal }),
  });

  // The remaining keys start with the entity prefix the realtime map uses for that kind of
  // change, so each section refreshes on its own without invalidating the whole page.
  /*
   * Every job, not only the open ones.
   *
   * This asked for `open=true`, a default that is right on the jobs screen — nobody opens a
   * work queue to read history — and exactly wrong here. A customer's file *is* the history:
   * the question it answers is "what have we done for these people", and the answer for a
   * customer whose work is finished was an empty panel reading "no open work". 1 791 of
   * Hydrokleen's customers have nothing but closed jobs, so for most of the book the file
   * looked like a customer we had never served.
   */
  const jobs = useQuery({
    queryKey: ['jobs', { customerId, limit: FETCH_LIMIT }],
    queryFn: ({ signal }) =>
      request<CustomerJobsResponse>(`/jobs?customerId=${customerId}&limit=${FETCH_LIMIT}`, { signal }),
  });

  const quotes = useQuery({
    queryKey: ['quotes', { customerId, limit: FETCH_LIMIT }],
    queryFn: ({ signal }) =>
      request<CustomerQuotesResponse>(`/quotes?customerId=${customerId}&limit=${FETCH_LIMIT}`, { signal }),
  });

  const invoices = useQuery({
    queryKey: ['invoices', { customerId, limit: FETCH_LIMIT }],
    queryFn: ({ signal }) =>
      request<CustomerInvoicesResponse>(`/invoices?customerId=${customerId}&limit=${FETCH_LIMIT}`, { signal }),
  });

  const callLog = useQuery({
    queryKey: ['calls', { customerId, limit: FETCH_LIMIT }],
    queryFn: ({ signal }) =>
      request<CustomerCallsResponse>(`/calls?customerId=${customerId}&limit=${FETCH_LIMIT}`, { signal }),
  });

  // Keyed under `threads` because that is the prefix invalidated when a message arrives —
  // a customer's reply must appear here without anyone opening the inbox.
  /*
   * The whole record, in one stream.
   *
   * This endpoint existed and nothing rendered it — every section on this page answered "what
   * do they owe" or "what is open", and none answered "what has happened with these people".
   * That is the question somebody has before they pick up the phone.
   */
  const history = useQuery({
    queryKey: ['customer', 'timeline', customerId],
    queryFn: ({ signal }) => request<{ items: TimelineEvent[] }>(`/customers/${customerId}/timeline?limit=200`, { signal }),
  });

  const thread = useQuery({
    queryKey: ['threads', { customerId, limit: FETCH_LIMIT }],
    queryFn: ({ signal }) =>
      request<CustomerThreadResponse>(`/customers/${customerId}/thread?limit=${FETCH_LIMIT}`, { signal }),
  });

  /** The pipeline as the duct run wants it: names localised, colour resolved from the kind. */
  const stages: DuctStage[] = useMemo(
    () =>
      (jobs.data?.pipeline ?? []).map((stage) => ({
        id: stage.id,
        name: localized(stage.name, locale),
        kind: stage.kind,
        color: STAGE_COLOR[stage.kind],
      })),
    [jobs.data, locale],
  );

  const expand = (section: typeof tab) => {
    void navigate({
      to: '.',
      // Collapsing means "no section expanded", which is what `overview` is.
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: prev.tab === section ? 'overview' : section }),
      replace: true,
    });
  };

  /**
   * A deep link arrives with the section — and sometimes the exact call — already chosen, and
   * the target is usually below the fold. Scrolling once the section has rendered is what
   * makes `?tab=calls&call=<id>` from the global search actually land on the call.
   */
  const loaded = callLog.isSuccess && invoices.isSuccess && quotes.isSuccess && jobs.isSuccess;
  useEffect(() => {
    if (tab === 'overview' || !loaded) return;
    const target = (call && document.getElementById(`call-${call}`)) || document.getElementById(`section-${tab}`);
    target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [tab, call, loaded]);

  if (customer.isPending) {
    return <p className="screen__loading">{t('common.loading')}</p>;
  }

  if (customer.isError || !customer.data) {
    return (
      <div className="notice notice--error" role="alert">
        <p>{t('errors.notFound')}</p>
        <Link to="/customers" className="btn">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  const c = customer.data;
  const primaryPhone = c.phones.find((p) => p.isPrimary) ?? c.phones[0] ?? null;
  const primaryEmail = c.emails.find((e) => e.isPrimary && !e.bounced) ?? c.emails[0] ?? null;
  const serviceAddress = c.addresses.find((a) => a.isPrimary && a.kind === 'service') ?? c.addresses[0] ?? null;
  const openBalance = toCents(c.balance.open);
  const overdueBalance = toCents(c.balance.overdue);
  const credit = toCents(c.balance.unappliedCredit);

  return (
    <div className="screen screen--customer">
      <Link to="/customers" className="screen__breadcrumb">
        {t('common.back')} · {t('customer.many')}
      </Link>

      <header className="cust-head">
        <div className="cust-head__identity">
          <h1 className="cust-head__name">{c.displayName}</h1>
          {c.organization && <p className="cust-head__org">{c.organization}</p>}

          <div className="cust-head__meta">
            {c.number && (
              // Staff read this number out on the phone, so it is labelled rather than left as
              // a bare figure a screen reader would announce without context.
              <span className="cust-head__number">
                <span className="label">Nº</span>
                <span className="tnum">{c.number}</span>
              </span>
            )}
            {/* Correspondence language sits in the header because it decides what the customer
                receives: sending a francophone an English invoice is a visible mistake. */}
            <span className="cust-head__locale" title={t('customer.language')}>
              {LOCALE_NAME[c.locale]}
            </span>
            {c.status !== 'active' && (
              <span className="chip" style={{ ['--chip' as string]: 'var(--text-muted)' }}>
                {t(`customer.status.${c.status}`)}
              </span>
            )}
            {/* Correcting a number or a gate code starts here. In the meta row rather than as a
                prominent button: this page is for reading, and editing is the rarer act. */}
            <Link to="/customers/$customerId/edit" params={{ customerId: c.id }} className="cust-head__edit">
              {t('common.edit')}
            </Link>
          </div>

          {c.tags.length > 0 && (
            <div className="tagrow">
              {c.tags.map((tag) => (
                <span key={tag.id} className="tag" style={{ ['--chip' as string]: tag.color }}>
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          {/* Billing follows the parent account, so anyone about to invoice this address needs
              to see the parent before they raise the document, not after. */}
          {c.billToParent && c.parent && (
            <p className="cust-head__parent">
              <span className="label">{t('customer.billToParent')}</span>
              <Link to="/customers/$customerId" params={{ customerId: c.parent.id }}>
                {c.parent.organization ?? c.parent.displayName}
              </Link>
            </p>
          )}
        </div>

        <div className="cust-head__contact">
          <span className="label">{t('customer.phone')}</span>
          {primaryPhone ? (
            <p className="contact__row">
              {/* Click-to-call: the phone is this business's primary channel, so reaching the
                  customer must be one press from the top of their page. */}
              <a
                href={`tel:${toE164(primaryPhone.raw)}`}
                className="phone contact__phone"
                aria-label={`${t('calls.dial')} ${formatPhone(primaryPhone.raw, locale)}`}
              >
                {formatPhone(primaryPhone.raw, locale)}
              </a>
              {primaryPhone.extension && <span className="phone"> p. {primaryPhone.extension}</span>}
              <CopyButton value={primaryPhone.raw} t={t} />
              {!primaryPhone.smsConsent && (
                <span className="chip" style={{ ['--chip' as string]: 'var(--state-caution)' }}>
                  {t('customer.noSmsConsent')}
                </span>
              )}
            </p>
          ) : (
            <p className="contact__row contact__row--none">{t('common.none')}</p>
          )}

          <span className="label">{t('customer.email')}</span>
          {primaryEmail ? (
            <p className="contact__row">
              <a href={`mailto:${primaryEmail.email}`} className="contact__email">
                {primaryEmail.email}
              </a>
              <CopyButton value={primaryEmail.email} t={t} />
              {primaryEmail.bounced && (
                <span className="chip" style={{ ['--chip' as string]: 'var(--state-negative)' }}>
                  {t('customer.bounced')}
                </span>
              )}
            </p>
          ) : (
            <p className="contact__row contact__row--none">{t('common.none')}</p>
          )}
        </div>

        <div className="cust-head__address">
          <span className="label">{t('customer.serviceAddress')}</span>
          {serviceAddress ? (
            <address className="address">
              {/*
                The address opens its own history. A house outlasts the people in it: somebody
                sells, the new owner rings about the same unit, and the work done for the last
                owner is filed under a customer nobody thinks to look up.
              */}
              <Link to="/addresses/$addressId" params={{ addressId: serviceAddress.id }} className="address__link">
                {serviceAddress.line1}
              </Link>
              {serviceAddress.line2 && <span>{serviceAddress.line2}</span>}
              <span>
                {serviceAddress.city}, {serviceAddress.region}
                {serviceAddress.postalCode && <span className="tnum"> {serviceAddress.postalCode}</span>}
              </span>

              {/*
                Two counts, never merged. Somebody who preceded this customer in this home is
                the useful signal; the other 81 flats in the block are not, and one badge for
                both would make an apartment building look like a much-sold house.
              */}
              {serviceAddress.othersAtPlace > 0 && (
                <Link
                  to="/addresses/$addressId"
                  params={{ addressId: serviceAddress.id }}
                  className="address__badge address__badge--place"
                >
                  {t('address.othersAtPlace', { count: serviceAddress.othersAtPlace })}
                </Link>
              )}
              {serviceAddress.othersAtPlace === 0 && serviceAddress.othersInBuilding > 0 && (
                <Link
                  to="/addresses/$addressId"
                  params={{ addressId: serviceAddress.id }}
                  search={{ scope: 'building' } as never}
                  className="address__badge"
                >
                  {t('address.othersInBuilding', { count: serviceAddress.othersInBuilding })}
                </Link>
              )}
              {/* The access note is the single most operationally valuable field on this
                  screen: a gate code nobody read is a wasted trip, so it is given the copper
                  accent rather than being filed under "notes". */}
              {serviceAddress.note && (
                <span className="access-note">
                  <span className="label">{t('customer.accessNote')}</span>
                  {serviceAddress.note}
                </span>
              )}
            </address>
          ) : (
            <p className="contact__row--none">{t('common.none')}</p>
          )}
        </div>

        <div className="cust-head__money">
          <span className="label">{t('customer.balance')}</span>
          <p className={`balance__amount money${overdueBalance > 0 ? ' money--overdue' : ''}`}>
            {formatMoney(openBalance, locale)}
          </p>
          {overdueBalance > 0 && (
            <p className="balance__note balance__note--overdue">
              {t('customer.overduePortion', { amount: formatMoney(overdueBalance, locale) })}
            </p>
          )}
          {credit > 0 && (
            <p className="balance__note balance__note--credit">
              {t('customer.credit', { amount: formatMoney(credit, locale) })}
            </p>
          )}
        </div>
      </header>

      {c.note && (
        <p className="cust-note">
          <span className="label">{t('customer.internalNote')}</span>
          {c.note}
        </p>
      )}

      <Section
        id="jobs"
        title={t('customer.openWork')}
        total={jobs.data?.total}
        expanded={tab === 'jobs'}
        onExpand={expand}
        t={t}
      >
        {jobs.isError && <SectionError onRetry={() => void jobs.refetch()} t={t} />}
        {jobs.data?.items.length === 0 && <p className="section__empty">{t('customer.noWork')}</p>}
        <ul className="joblist">
          {(tab === 'jobs' ? jobs.data?.items : jobs.data?.items.slice(0, PREVIEW))?.map((job) => (
            <li key={job.id} className="joblist__row">
              <Link to="/jobs/$jobId" params={{ jobId: job.id }} className="joblist__title">
                <span className="tnum joblist__number">{job.number}</span>
                {job.title ?? t('job.one')}
              </Link>

              {/* The duct run: where this job sits in the shop, readable without a legend. */}
              {stages.length > 0 && (
                <DuctRun stages={stages} currentStageId={job.stageId} variant="row" className="joblist__duct" />
              )}

              <span className="joblist__when">
                {job.scheduledStart ? (
                  // A scheduled window is a measured span, which is exactly — and only —
                  // what the dimension rule is for.
                  <span className="dimension">
                    <time dateTime={job.scheduledStart}>{formatDayTime(job.scheduledStart, locale)}</time>
                    {job.scheduledEnd && <time dateTime={job.scheduledEnd}>{formatTime(job.scheduledEnd, locale)}</time>}
                  </span>
                ) : (
                  <span className="joblist__unscheduled">{t('job.unscheduled')}</span>
                )}
              </span>

              <span className="joblist__crew">
                {job.assignees.length > 0
                  ? job.assignees.map((a) => a.name).join(', ')
                  : t('job.unassigned')}
              </span>

              <span className="money joblist__balance">{formatMoney(toCents(job.balance), locale)}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        id="quotes"
        title={t('quote.many')}
        total={quotes.data?.total}
        expanded={tab === 'quotes'}
        onExpand={expand}
        t={t}
      >
        {quotes.isError && <SectionError onRetry={() => void quotes.refetch()} t={t} />}
        {quotes.data?.items.length === 0 && <p className="section__empty">{t('common.noResults')}</p>}
        {quotes.data && quotes.data.items.length > 0 && (
          <table className="table table--dense" aria-label={t('quote.many')}>
            <thead>
              <tr>
                <th scope="col">
                  <span className="label">{t('quote.one')}</span>
                </th>
                <th scope="col">
                  <span className="label">{t('job.one')}</span>
                </th>
                <th scope="col">
                  <span className="label">{t('common.status')}</span>
                </th>
                <th scope="col" className="numeric">
                  <span className="label">{t('invoice.total')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(tab === 'quotes' ? quotes.data.items : quotes.data.items.slice(0, PREVIEW)).map((quote) => (
                <tr key={quote.id}>
                  <th scope="row">
                    {/* No quote page exists in the route tree yet, so the honest destination
                        is the quote list filtered to this number rather than a dead link. */}
                    <Link to="/quotes" search={{ q: quote.number }} className="tnum">
                      {quote.number}
                    </Link>
                    {quote.issuedOn && (
                      <span className="cell__sub">
                        <time dateTime={quote.issuedOn}>{formatDay(quote.issuedOn, locale)}</time>
                      </span>
                    )}
                  </th>
                  <td>
                    {quote.jobId && quote.jobNumber ? (
                      <Link to="/jobs/$jobId" params={{ jobId: quote.jobId }} className="tnum">
                        {quote.jobNumber}
                      </Link>
                    ) : (
                      <span className="cell__none" aria-hidden="true">
                        —
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="chip" style={{ ['--chip' as string]: QUOTE_STATUS_COLOR[quote.status] }}>
                      {t(`quote.status.${quote.status}`)}
                    </span>
                    {quote.acceptedAt && (
                      <span className="cell__sub">
                        {t('quote.acceptedOn', { date: formatDay(quote.acceptedAt, locale) })}
                      </span>
                    )}
                  </td>
                  <td className="numeric">
                    <span className="money">{formatMoney(toCents(quote.total), locale)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        id="invoices"
        title={t('invoice.many')}
        total={invoices.data?.total}
        expanded={tab === 'invoices'}
        onExpand={expand}
        t={t}
      >
        {invoices.isError && <SectionError onRetry={() => void invoices.refetch()} t={t} />}
        {invoices.data?.items.length === 0 && <p className="section__empty">{t('common.noResults')}</p>}
        {invoices.data && invoices.data.items.length > 0 && (
          <table className="table table--dense" aria-label={t('invoice.many')}>
            <thead>
              <tr>
                <th scope="col">
                  <span className="label">{t('invoice.one')}</span>
                </th>
                <th scope="col">
                  <span className="label">{t('common.dueDate')}</span>
                </th>
                <th scope="col" className="numeric">
                  <span className="label">{t('invoice.total')}</span>
                </th>
                <th scope="col" className="numeric">
                  <span className="label">{t('invoice.balance')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(tab === 'invoices' ? invoices.data.items : invoices.data.items.slice(0, PREVIEW)).map(
                (invoice) => {
                  const balance = toCents(invoice.balance);
                  const late = daysOverdue(invoice.dueOn, balance, invoice.status);
                  return (
                    // An overdue invoice is why most of these calls happen, so the row itself
                    // carries the state — not a badge the eye has to find.
                    <tr key={invoice.id} className={late > 0 ? 'tr--overdue' : undefined}>
                      <th scope="row">
                        <Link to="/invoices" search={{ q: invoice.number }} className="tnum">
                          {invoice.number}
                        </Link>
                        <span className="cell__sub">
                          <span className="chip" style={{ ['--chip' as string]: INVOICE_STATUS_COLOR[invoice.status] }}>
                            {t(`invoice.status.${invoice.status}`)}
                          </span>
                          {invoice.jobId && invoice.jobNumber && (
                            <Link to="/jobs/$jobId" params={{ jobId: invoice.jobId }} className="tnum">
                              {invoice.jobNumber}
                            </Link>
                          )}
                        </span>
                      </th>
                      <td>
                        {invoice.dueOn ? (
                          <time dateTime={invoice.dueOn}>{formatDay(invoice.dueOn, locale)}</time>
                        ) : (
                          <span className="cell__none" aria-hidden="true">
                            —
                          </span>
                        )}
                        {late > 0 && <span className="cell__sub cell__sub--overdue">{t('invoice.overdue', { count: late })}</span>}
                        {/* Before chasing a payment, staff need to know the customer ever saw
                            the invoice. Often they did not. */}
                        {balance > 0 && !invoice.firstViewedAt && (
                          <span className="cell__sub">{t('invoice.neverOpened')}</span>
                        )}
                      </td>
                      <td className="numeric">
                        <span className="money">{formatMoney(toCents(invoice.total), locale)}</span>
                      </td>
                      <td className="numeric">
                        {balance > 0 ? (
                          <span className={`money${late > 0 ? ' money--overdue' : ' money--due'}`}>
                            {formatMoney(balance, locale)}
                          </span>
                        ) : (
                          <span className="money money--settled">{formatMoney(0, locale)}</span>
                        )}
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={3}>
                  <span className="label">{t('invoice.balance')}</span>
                </th>
                <td className="numeric">
                  <span className="money money--due">{formatMoney(toCents(invoices.data.outstanding), locale)}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </Section>

      <Section
        id="calls"
        title={t('nav.calls')}
        total={callLog.data?.total}
        expanded={tab === 'calls'}
        onExpand={expand}
        t={t}
      >
        {callLog.isError && <SectionError onRetry={() => void callLog.refetch()} t={t} />}
        {callLog.data?.items.length === 0 && <p className="section__empty">{t('common.noResults')}</p>}
        <ul className="calllist">
          {(tab === 'calls' ? callLog.data?.items : callLog.data?.items.slice(0, PREVIEW))?.map((entry) => (
            <li
              key={entry.id}
              id={`call-${entry.id}`}
              className={`call${call === entry.id ? ' call--focused' : ''}`}
              aria-current={call === entry.id ? 'true' : undefined}
            >
              <span className="call__head">
                <span className={`call__direction call__direction--${entry.direction}`}>
                  {t(entry.status === 'no_answer' ? 'calls.missed' : `calls.${entry.direction}`)}
                </span>
                <time dateTime={entry.startedAt} className="call__when">
                  {formatDayTime(entry.startedAt, locale)}
                </time>
                <span className="call__duration tnum">{formatDuration(entry.durationSeconds)}</span>
                {entry.handledByName && <span className="call__staff">{entry.handledByName}</span>}
                {entry.jobId && entry.jobNumber && (
                  <Link to="/jobs/$jobId" params={{ jobId: entry.jobId }} className="tnum">
                    {entry.jobNumber}
                  </Link>
                )}
              </span>

              {/* The summary, never the verbatim transcript. Staff scanning a history need
                  "Virginie a offert un rabais de 30 $ sur le mural" far more often than six
                  hundred words of dialogue; the full text stays one press away on the call. */}
              {entry.transcriptSummary ? (
                <p className="call__summary">
                  <span className="label">{t('calls.summary')}</span>
                  {entry.transcriptSummary}
                </p>
              ) : (
                entry.hasTranscript && <p className="call__summary call__summary--pending">{t('calls.summaryPending')}</p>
              )}

              <span className="call__media">
                {entry.hasRecording && <span className="chip chip--quiet">{t('calls.recording')}</span>}
                {entry.hasTranscript && <span className="chip chip--quiet">{t('calls.transcript')}</span>}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        id="messages"
        title={t('nav.inbox')}
        total={thread.data?.thread?.messageCount}
        expanded={tab === 'messages'}
        onExpand={expand}
        previewCount={PREVIEW_MESSAGES}
        t={t}
      >
        {thread.isError && <SectionError onRetry={() => void thread.refetch()} t={t} />}
        {thread.data && !thread.data.thread && <p className="section__empty">{t('common.noResults')}</p>}
        <ol className="msglist">
          {(tab === 'messages'
            ? thread.data?.messages
            : thread.data?.messages.slice(-PREVIEW_MESSAGES)
          )?.map((message) => (
            <li key={message.id} className={`msg msg--${message.authorKind}`}>
              <span className="msg__head">
                <span className="msg__author">
                  {message.authorName ?? (message.authorKind === 'customer' ? c.displayName : t('customer.one'))}
                </span>
                {/* How it travelled, always shown: a staff-only `note` must never be mistaken
                    for something the customer actually received. */}
                <span className="chip chip--quiet">{t(`message.transport.${message.transport}`)}</span>
                <time dateTime={message.sentAt} className="msg__when">
                  {formatDayTime(message.sentAt, locale)}
                </time>
                {message.failedReason && (
                  <span className="chip" style={{ ['--chip' as string]: 'var(--state-negative)' }}>
                    {message.failedReason}
                  </span>
                )}
              </span>
              <p className="msg__body">{message.body}</p>
            </li>
          ))}
        </ol>
        {thread.data?.thread && (
          <Link to="/inbox" search={{ thread: thread.data.thread.id }} className="btn">
            {t('customer.viewThread')}
          </Link>
        )}
      </Section>

      {/*
        The history, in the periods it actually happened in.
        
        Three years of entries listed flat is a wall in which the shape — *a lot of work for
        these people in spring 2025, nothing since* — is invisible. The bursts are found in the
        data rather than cut on calendar boundaries, because a job beginning on 28 December is
        one episode and not two.
      */}
      <Section
        id="history"
        title={t('customer.history')}
        total={history.data?.items.length}
        expanded={tab === 'history'}
        onExpand={expand}
        previewCount={0}
        t={t}
      >
        {history.isError && <SectionError onRetry={() => void history.refetch()} t={t} />}
        {history.isPending && <p className="screen__loading">{t('common.loading')}</p>}
        {history.data?.items.length === 0 && <p className="section__empty">{t('customer.noHistory')}</p>}

        {groupIntoPeriods(history.data?.items ?? [], (event) => event.at, locale)
          .slice(0, tab === 'history' ? undefined : 2)
          .map((period) => (
            <div key={period.start.toISOString()} className="period">
              <h3 className="period__head" title={formatSpanExact(period.start, period.end, locale)}>
                <span className="period__label">{period.label}</span>
                <span className="period__rule" aria-hidden="true" />
                <span className="period__count">{period.items.length}</span>
              </h3>
              <Timeline events={period.items} />
            </div>
          ))}
      </Section>

      {/*
        This customer's file drawer: every photograph and document filed against any of their
        jobs, newest first. Deliberately not a `Section` — the expand toggle lives in the
        `tab` search parameter, whose values are fixed by the route, and a band that is always
        fully shown does not need one anyway.
      */}
      <AttachmentSection scope={{ customerId }} />
    </div>
  );
}

/**
 * One band of the relationship.
 *
 * Deliberately no `aria-expanded`: the section is never hidden, and claiming otherwise to a
 * screen reader would be a lie. The button toggles between the first few rows and all of
 * them, which is what the `tab` parameter actually means here.
 */
function Section({
  id,
  title,
  total,
  expanded,
  onExpand,
  previewCount = PREVIEW,
  t,
  children,
}: {
  id: 'jobs' | 'quotes' | 'invoices' | 'calls' | 'messages' | 'history';
  title: string;
  total?: number;
  expanded: boolean;
  onExpand: (section: 'jobs' | 'quotes' | 'invoices' | 'calls' | 'messages' | 'history') => void;
  /** How many rows the collapsed body shows, so the toggle only appears when it does something. */
  previewCount?: number;
  t: (key: string, vars?: Record<string, string | number>) => string;
  children: React.ReactNode;
}) {
  const showToggle = (total ?? 0) > previewCount;
  return (
    <section
      id={`section-${id}`}
      className={`section${expanded ? ' section--expanded' : ''}`}
      aria-labelledby={`section-${id}-title`}
    >
      <header className="section__head">
        <h2 id={`section-${id}-title`} className="section__title">
          {title}
        </h2>
        {total != null && <span className="section__count tnum">{total}</span>}
        {showToggle && (
          <button type="button" className="section__toggle" onClick={() => onExpand(id)}>
            {expanded ? t('customer.showLess') : t('customer.showAll', { count: total ?? 0 })}
          </button>
        )}
      </header>
      <div className="section__body">{children}</div>
    </section>
  );
}

/**
 * One section failing must not take the page down: the rest of the relationship is still
 * worth reading while the invoices are refetched.
 */
function SectionError({ onRetry, t }: { onRetry: () => void; t: (key: string) => string }) {
  return (
    <p className="notice notice--error" role="alert">
      {t('errors.generic')}{' '}
      <button type="button" onClick={onRetry}>
        {t('common.retry')}
      </button>
    </p>
  );
}

/**
 * Copy to clipboard.
 *
 * The "copié" flash is the one piece of `useState` on this screen, and it qualifies: it is
 * transient acknowledgement nobody would want restored by a reload, unlike a filter.
 */
function CopyButton({ value, t }: { value: string; t: (key: string) => string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      className="copy"
      aria-label={`${t('common.copy')} ${value}`}
      onClick={() => {
        void navigator.clipboard
          ?.writeText(value)
          .then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          })
          // A browser that refuses clipboard access is not worth an error dialog; the number
          // is on screen and selectable either way.
          .catch(() => undefined);
      }}
    >
      {copied ? t('common.copied') : t('common.copy')}
    </button>
  );
}

/**
 * Dates are formatted locally because there is no shared date module yet, and the two screens
 * need different things: the list wants "how cold is this?", this page wants the actual day a
 * document was issued or a call was taken.
 */
function intl(locale: Locale): string {
  return locale === 'fr' ? 'fr-CA' : 'en-CA';
}

function formatDay(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intl(locale), { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(iso),
  );
}

function formatDayTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intl(locale), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intl(locale), { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/**
 * Call length reads as an instrument value: `4 min 12 s`, never `252 seconds`. Unit symbols
 * rather than words, so the form is identical in both interface languages.
 */
function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${String(seconds % 60).padStart(2, '0')} s`;
}

/**
 * Days past due, computed here rather than trusted from a server field: a page left open
 * overnight would otherwise keep showing yesterday's count.
 */
function daysOverdue(dueOn: string | null, balanceCents: number, status: string): number {
  if (!dueOn || balanceCents <= 0 || status === 'paid' || status === 'voided' || status === 'draft') return 0;
  const days = Math.floor((Date.now() - new Date(dueOn).getTime()) / 86_400_000);
  return days > 0 ? days : 0;
}
